import React from 'react';
import { ButtonProps } from '../../../../../components/menus/buttons/types';
import { useTranslation } from 'react-i18next';
import SidePanel from '../../../../../components/menus/SidePanel/SidePanel';
import ModalHeader from '@wix/da-ds/pkg/modals/dsPresets/Modal/ModalHeader';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import Flex from '@wix/da-ds/pkg/Flex';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import LayoutRadioButton from '../../../../../components/lego/LayoutRadioButton';
import EditorLayoutGrid from '@wix/da-ds/pkg/Icons/24x24/EditorLayoutGrid';
import EditorLayoutTorpedo from '@wix/da-ds/pkg/Icons/24x24/EditorLayoutTorpedo';
import useSettingsState from './useSettingsState';
import {
  getCurrentUser,
  getExtensionOptions,
} from '../../../../../components/utils/tiptap';
import { DaGalleryOptions } from '../../../gallery';
import { ListItemRadioButton } from '@wix/da-ds/pkg/List/ListItem';
import NumericField from '../../../../../components/lego/NumericField';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import {
  addMissingProtocol,
  validateHref,
} from '../../../../../components/utils/validateHref';

import s from './Settings.scss';

const SettingsForm: React.FC<ButtonProps & { onClose: any }> = ({
  editor,
  onClose,
}) => {
  const { t } = useTranslation('editor');
  const closeSettings = () => {
    onClose(false);
  };
  const options = getExtensionOptions<DaGalleryOptions>(editor, 'da-gallery');
  const attrs = editor.getAttributes('da-gallery');
  const {
    state,
    setGalleryLayout,
    setGalleryWidth,
    setGridItemWidth,
    setGridItemHeight,
    setLayoutSpacing,
    setTorpedoRowHeight,
    setItemCropping,
    setRowAlignment,
    updateLinkFor,
  } = useSettingsState(editor);

  const currentUser = getCurrentUser(editor);
  const atLeastOneCanLink = attrs.deviations?.some(
    d => d.author?.userId === currentUser?.userId
  );

  return (
    <SidePanel
      editor={editor}
      onRequestClose={closeSettings}
      header={<ModalHeader heading={t('ext.gallery.settings.title')} />}
      footer={
        <SolidButton
          size="medium"
          variant="approval-green"
          onClick={closeSettings}
        >
          {t('ext.gallery.settings.btn.done')}
        </SolidButton>
      }
    >
      <Flex direction="column" gap={16}>
        <fieldset className={s['fieldset']}>
          <InputLabelText className={s['legend']}>
            {t('ext.gallery.settings.container')}
          </InputLabelText>
          <NumericField
            label={t('ext.gallery.settings.container.width')}
            value={state.width!}
            onChange={setGalleryWidth}
            maxValue={options.formMaxWidth ?? options.maxWidth ?? 780}
            minValue={options.minWidth ?? 10}
            sliderStep={1}
          />
        </fieldset>

        <fieldset className={s['fieldset']} role="radiogroup">
          <InputLabelText className={s['legend']}>
            {t('ext.gallery.settings.layout')}
          </InputLabelText>

          <div className={s['layout-radio-group']}>
            <LayoutRadioButton
              label={t('ext.gallery.settings.layout.grid')}
              value={'grid'}
              onChange={setGalleryLayout}
              checked={state.layout === 'grid'}
              icon={EditorLayoutGrid}
            />
            <LayoutRadioButton
              label={t('ext.gallery.settings.layout.torpedo')}
              value={'torpedo'}
              onChange={setGalleryLayout}
              checked={state.layout === 'torpedo'}
              icon={EditorLayoutTorpedo}
            />
          </div>
        </fieldset>

        {state.layout === 'grid' && (
          <fieldset className={s['fieldset']}>
            <InputLabelText className={s['legend']}>
              {t('ext.gallery.settings.gridSettings')}
            </InputLabelText>

            <NumericField
              label={t('ext.gallery.settings.gridSettings.thumbWidth')}
              value={state.gridItemWidth!}
              onChange={setGridItemWidth}
              minValue={10}
              maxValue={400}
              sliderStep={10}
            />

            <NumericField
              label={t('ext.gallery.settings.gridSettings.thumbHeight')}
              value={state.gridItemHeight!}
              onChange={setGridItemHeight}
              minValue={10}
              maxValue={400}
              sliderStep={10}
            />

            <NumericField
              label={t('ext.gallery.settings.spacing')}
              value={state.layoutSpacing!}
              onChange={setLayoutSpacing}
            />
          </fieldset>
        )}

        {state.layout === 'torpedo' && (
          <fieldset className={s['fieldset']}>
            <InputLabelText className={s['legend']}>
              {t('ext.gallery.settings.masonrySettings')}
            </InputLabelText>

            <NumericField
              label={t('ext.gallery.settings.masonrySettings.rowHeight')}
              value={state.torpedoRowHeight!}
              onChange={setTorpedoRowHeight}
              minValue={10}
              maxValue={400}
              sliderStep={10}
            />

            <NumericField
              label={t('ext.gallery.settings.spacing')}
              value={state.layoutSpacing!}
              onChange={setLayoutSpacing}
            />
          </fieldset>
        )}

        <fieldset className={s['fieldset']} role="radiogroup">
          <InputLabelText className={s['legend']}>
            {t('ext.gallery.settings.cropping')}
          </InputLabelText>

          <ListItemRadioButton
            compact
            name={'cropping'}
            value={'crop'}
            label={t('ext.gallery.settings.cropping.crop')}
            onChange={setItemCropping}
            checked={state.itemCropping === 'crop'}
          />
          <ListItemRadioButton
            compact
            name={'cropping'}
            value={'fit'}
            label={t('ext.gallery.settings.cropping.fit')}
            onChange={setItemCropping}
            checked={state.itemCropping === 'fit'}
          />
        </fieldset>

        <fieldset className={s['fieldset']} role="radiogroup">
          <InputLabelText className={s['legend']}>
            {t('ext.gallery.settings.blockalign')}
          </InputLabelText>

          <ListItemRadioButton
            compact
            name={'innerAlignment'}
            value={'left'}
            label={t('ext.gallery.settings.blockalign.left')}
            onChange={setRowAlignment}
            checked={state.layoutRowAlignment === 'left'}
          />
          <ListItemRadioButton
            compact
            name={'innerAlignment'}
            value={'center'}
            label={t('ext.gallery.settings.blockalign.center')}
            onChange={setRowAlignment}
            checked={state.layoutRowAlignment === 'center'}
          />
          <ListItemRadioButton
            compact
            name={'innerAlignment'}
            value={'right'}
            label={t('ext.gallery.settings.blockalign.right')}
            onChange={setRowAlignment}
            checked={state.layoutRowAlignment === 'right'}
          />
        </fieldset>

        {options.canLink && atLeastOneCanLink && (
          <fieldset className={s['fieldset']}>
            <InputLabelText className={s['legend']}>
              {t('ext.gallery.settings.links.heading')}
            </InputLabelText>
            <p className={s['links-subheading']}>
              {t('ext.gallery.settings.links.subheading')}
            </p>

            <div className={s['links']}>
              {attrs.deviations.map((dev, i) => {
                const allowLink = dev.author?.userId === currentUser?.userId;
                if (!allowLink) {
                  return null;
                }

                return (
                  <div key={dev.deviationId}>
                    <TextInput
                      id={dev.deviationId}
                      label={`Item ${i + 1}`}
                      value={state.linkOverrides[dev.deviationId] ?? ''}
                      onChange={e =>
                        updateLinkFor(dev.deviationId, e.target.value)
                      }
                      placeholder={t('ext.gallery.settings.links.placeholder')}
                      validationResult={
                        (state.linkOverrides?.[dev.deviationId] ?? '') === ''
                          ? undefined
                          : validateHref(
                              addMissingProtocol(
                                state.linkOverrides?.[dev.deviationId]
                              )
                            )
                          ? 'success'
                          : 'error'
                      }
                      onFocus={e => {
                        setTimeout(() => {
                          e.target.scrollIntoView();
                        }, 300);
                      }}
                    ></TextInput>
                  </div>
                );
              })}
            </div>
          </fieldset>
        )}
      </Flex>
    </SidePanel>
  );
};
SettingsForm.displayName = 'SettingsForm';

export default SettingsForm;
